import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Define the interface for user parameters
export interface UserParams {
  EMPIMAGE?: string | null;
  EMPNAME?: string | null;
  MESSAGE?: string | null;
  RESTNAME?: string | null;
  STAFFID?: string | number | null;
  STAFFMAPLOCODE?: string | null;
  SYSDATE?: string | null;
  LOCTIMEZONE?: string | null;
  USERREFCODE?: string | number | null;
  COMPCODE?: string | null;
  LOCCODE?: string | null;
  CSCODE?: string | null;
  COMPNAME?: string | null;
  LOGSTST?: string | null;
  COMPANYLOGO?: string | null;
  STYPE?: string | null;
  STOREDATE?: string | null;
  DASHBOARDSTYLE?: string | null;
  USERNAME?: string | null;
  GROUPNAME?: string | null;
  GROUPCODE?: string | number | null;
  TEMPKEY?: string | null;
  TOKEN?: string | null;
  [key: string]: any; // Allow for additional properties
}

// Define the state interface
export interface UserState {
  EMPIMAGE: string | null;
  EMPNAME: string | null;
  MESSAGE: string | null;
  RESTNAME: string | null;
  STAFFID: string | number | null;
  STAFFMAPLOCODE: string | null;
  SYSDATE: string | null;
  LOCTIMEZONE: string | null;
  USERREFCODE: string | number | null;
  COMPCODE: string | null;
  LOCCODE: string | null;
  CSCODE: string | null;
  COMPNAME: string | null;
  LOGSTST: string | null;
  COMPANYLOGO: string | null;
  STYPE: string | null;
  STOREDATE: string | null;
  DASHBOARDSTYLE: string | null;
  USERNAME: string | null;
  GROUPNAME: string | null;
  GROUPCODE: string | number | null;
  TEMPKEY: string | null;
  TOKEN: string | null;
  USER_PARAM?: UserParams; // Add USER_PARAM as an optional property
}

// Define initial state for user
const initialState: UserState = {
  EMPIMAGE: null,
  EMPNAME: null,
  MESSAGE: null,
  RESTNAME: null,
  STAFFID: null,
  STAFFMAPLOCODE: null,
  SYSDATE: null,
  LOCTIMEZONE: null,
  USERREFCODE: null,
  COMPCODE: null,
  LOCCODE: null,
  CSCODE: null,
  COMPNAME: null,
  LOGSTST: null,
  COMPANYLOGO: null,
  STYPE: null,
  STOREDATE: null,
  DASHBOARDSTYLE: null,
  USERNAME: null,
  GROUPNAME: null,
  GROUPCODE: null,
  TEMPKEY: null,
  TOKEN: null,
};

// Create the user slice with proper typing
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // Action to set user parameters
    setUserParams: (state: UserState, action: PayloadAction<UserParams>) => {
      console.log("Payload received:", action.payload);
      
      // Set USER_PARAM with the payload
      state.USER_PARAM = { ...action.payload };
      
      // Set default logo or use provided one
      const randomLogo = "src/assets/images/shield2.png";
      state.COMPANYLOGO = action.payload.COMPANYLOGO || randomLogo;
      
      // Set other properties if they exist in the payload
      state.USERREFCODE = action.payload.USERREFCODE ?? state.USERREFCODE;
      state.TEMPKEY = action.payload.TEMPKEY || "";
      
      // Optional: Update individual fields if they're in the payload
      if (action.payload.EMPIMAGE !== undefined) state.EMPIMAGE = action.payload.EMPIMAGE;
      if (action.payload.EMPNAME !== undefined) state.EMPNAME = action.payload.EMPNAME;
      if (action.payload.MESSAGE !== undefined) state.MESSAGE = action.payload.MESSAGE;
      if (action.payload.RESTNAME !== undefined) state.RESTNAME = action.payload.RESTNAME;
      if (action.payload.STAFFID !== undefined) state.STAFFID = action.payload.STAFFID;
      if (action.payload.STAFFMAPLOCODE !== undefined) state.STAFFMAPLOCODE = action.payload.STAFFMAPLOCODE;
      if (action.payload.SYSDATE !== undefined) state.SYSDATE = action.payload.SYSDATE;
      if (action.payload.LOCTIMEZONE !== undefined) state.LOCTIMEZONE = action.payload.LOCTIMEZONE;
      if (action.payload.COMPCODE !== undefined) state.COMPCODE = action.payload.COMPCODE;
      if (action.payload.LOCCODE !== undefined) state.LOCCODE = action.payload.LOCCODE;
      if (action.payload.CSCODE !== undefined) state.CSCODE = action.payload.CSCODE;
      if (action.payload.COMPNAME !== undefined) state.COMPNAME = action.payload.COMPNAME;
      if (action.payload.LOGSTST !== undefined) state.LOGSTST = action.payload.LOGSTST;
      if (action.payload.STYPE !== undefined) state.STYPE = action.payload.STYPE;
      if (action.payload.STOREDATE !== undefined) state.STOREDATE = action.payload.STOREDATE;
      if (action.payload.DASHBOARDSTYLE !== undefined) state.DASHBOARDSTYLE = action.payload.DASHBOARDSTYLE;
      if (action.payload.USERNAME !== undefined) state.USERNAME = action.payload.USERNAME;
      if (action.payload.GROUPNAME !== undefined) state.GROUPNAME = action.payload.GROUPNAME;
      if (action.payload.GROUPCODE !== undefined) state.GROUPCODE = action.payload.GROUPCODE;
      if (action.payload.TOKEN !== undefined) state.TOKEN = action.payload.TOKEN;
    },
    
    // Action to reset user parameters to initial state
    resetUserParams: (): UserState => {
      return initialState;
    },
    
    // Optional: Action to update specific user fields
    updateUserField: (state: UserState, action: PayloadAction<Partial<UserState>>) => {
      return { ...state, ...action.payload };
    },
    
    // Optional: Action to set only company codes
    setCodes: (state: UserState, action: PayloadAction<{ COMPCODE?: string; LOCCODE?: string; RESTCODE?: string }>) => {
      const { COMPCODE, LOCCODE, RESTCODE } = action.payload;
      if (COMPCODE !== undefined) state.COMPCODE = COMPCODE;
      if (LOCCODE !== undefined) state.LOCCODE = LOCCODE;
      if (RESTCODE !== undefined) state.RESTNAME = RESTCODE; // Assuming RESTCODE maps to RESTNAME
    },
    
    // Optional: Action to set token
    setToken: (state: UserState, action: PayloadAction<string>) => {
      state.TOKEN = action.payload;
      if (state.USER_PARAM) {
        state.USER_PARAM.TOKEN = action.payload;
      }
    },
    
    // Optional: Action to clear token
    clearToken: (state: UserState) => {
      state.TOKEN = null;
      if (state.USER_PARAM) {
        state.USER_PARAM.TOKEN = null;
      }
    },
  },
});

// Export actions for use in components
export const { 
  setUserParams, 
  resetUserParams,
  updateUserField,
  setCodes,
  setToken,
  clearToken
} = userSlice.actions;

// Export selector functions
export const selectUser = (state: { user: UserState }): UserState => state.user;
export const selectToken = (state: { user: UserState }): string | null => state.user.TOKEN;
export const selectUserParam = (state: { user: UserState }): UserParams | undefined => state.user.USER_PARAM;
export const selectCompanyCode = (state: { user: UserState }): string | null => state.user.COMPCODE;
export const selectLocationCode = (state: { user: UserState }): string | null => state.user.LOCCODE;
export const selectUsername = (state: { user: UserState }): string | null => state.user.USERNAME;
export const selectStaffId = (state: { user: UserState }): string | number | null => state.user.STAFFID;
export const selectCompanyLogo = (state: { user: UserState }): string | null => state.user.COMPANYLOGO;

// Export reducer
export default userSlice.reducer;